import { useRef, useState } from "react";
import { HardDriveDownload, Upload, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import { clubApi } from "../../lib/clubApi";
import { toast } from "sonner";

export default function ClubBackup() {
    const [busy, setBusy] = useState(false);
    const inputRef = useRef(null);
    const [preview, setPreview] = useState(null);

    const doExport = async () => {
        setBusy(true);
        try {
            const data = await clubApi.exportBackup();
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            document.body.appendChild(a);
            a.href = url;
            a.download = `samir-club-backup-${new Date().toISOString().slice(0, 10)}.json`;
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success(`Backup scaricato · ${data.counts.members} clienti, ${data.counts.sales} vendite`);
        } catch { toast.error("Errore export"); }
        finally { setBusy(false); }
    };

    const onFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        try {
            const text = await f.text();
            const parsed = JSON.parse(text);
            if (parsed.app !== "samir-club") throw new Error("Non è un backup Samir Club");
            setPreview(parsed);
        } catch (err) {
            toast.error(`File non valido: ${err.message}`);
            setPreview(null);
        }
    };

    const doRestore = async (mode) => {
        if (!preview) return;
        if (mode === "replace" && !window.confirm("ATTENZIONE: sostituisco TUTTI i dati attuali con il backup. Continuare?")) return;
        setBusy(true);
        try {
            const r = await clubApi.restoreBackup({
                mode,
                members: preview.members || [],
                sales: preview.sales || [],
                payments: preview.payments || [],
            });
            toast.success(`Ripristino OK · +${r.members} clienti, +${r.sales} vendite, +${r.payments} pagamenti (${r.skipped} già presenti)`);
            setPreview(null);
            if (inputRef.current) inputRef.current.value = "";
        } catch (e) { toast.error(e.response?.data?.detail || "Errore restore"); }
        finally { setBusy(false); }
    };

    return (
        <div className="space-y-5" data-testid="club-backup-page">
            <h1 className="font-display text-2xl font-bold flex items-center gap-2">
                <HardDriveDownload className="text-orange-500" /> Backup
            </h1>
            <p className="text-sm text-stone-600">
                Esporta e conserva sempre una copia dei tuoi dati. Salva il file su Google Drive, chiavetta USB o mandalo a te stesso via email.
            </p>

            <div className="bg-white rounded-3xl p-5 border border-stone-200 space-y-3">
                <div className="flex items-center gap-2 mb-1">
                    <ShieldCheck size={20} className="text-emerald-600" />
                    <h2 className="font-display font-bold text-lg">Esporta i dati</h2>
                </div>
                <p className="text-sm text-stone-500">Scarichi un file JSON con TUTTI i tuoi clienti, vendite e pagamenti tessera.</p>
                <button
                    onClick={doExport}
                    disabled={busy}
                    className="w-full py-3 rounded-2xl bg-emerald-500 text-white font-bold active:bg-emerald-600 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    data-testid="club-backup-export"
                >
                    {busy ? <Loader2 size={18} className="animate-spin" /> : <HardDriveDownload size={18} />}
                    Scarica backup ora
                </button>
                <p className="text-xs text-stone-500">💡 <b>Consiglio:</b> fai un backup ogni domenica sera.</p>
            </div>

            <div className="bg-white rounded-3xl p-5 border border-stone-200 space-y-3">
                <div className="flex items-center gap-2 mb-1">
                    <Upload size={20} className="text-blue-600" />
                    <h2 className="font-display font-bold text-lg">Ripristina da backup</h2>
                </div>
                <p className="text-sm text-stone-500">Carica un file <code>.json</code> scaricato in precedenza.</p>
                <input
                    ref={inputRef}
                    type="file"
                    accept=".json,application/json"
                    onChange={onFile}
                    className="block w-full text-sm text-stone-600 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-blue-100 file:text-blue-700 file:font-bold"
                    data-testid="club-backup-file"
                />
                {preview && (
                    <div className="rounded-2xl border-2 border-blue-200 bg-blue-50 p-4 space-y-3">
                        <p className="text-sm font-bold text-blue-900">Backup pronto:</p>
                        <ul className="text-sm text-stone-700 space-y-0.5">
                            <li>👥 Clienti: <b>{preview.members?.length || 0}</b></li>
                            <li>🛒 Vendite: <b>{preview.sales?.length || 0}</b></li>
                            <li>💳 Pagamenti tessera: <b>{preview.payments?.length || 0}</b></li>
                            <li className="text-xs text-stone-500">Esportato il: {preview.exported_at ? new Date(preview.exported_at).toLocaleString("it-IT") : "?"}</li>
                        </ul>
                        <div className="grid grid-cols-2 gap-2 pt-1">
                            <button
                                onClick={() => doRestore("merge")}
                                disabled={busy}
                                className="py-2.5 rounded-xl bg-blue-500 text-white font-bold active:bg-blue-600 disabled:opacity-50"
                                data-testid="club-restore-merge"
                            >
                                Unisci
                            </button>
                            <button
                                onClick={() => doRestore("replace")}
                                disabled={busy}
                                className="py-2.5 rounded-xl bg-red-100 text-red-700 border border-red-300 font-bold active:bg-red-200 disabled:opacity-50"
                                data-testid="club-restore-replace"
                            >
                                Sostituisci tutto
                            </button>
                        </div>
                        <p className="text-xs text-stone-500 flex items-start gap-1">
                            <AlertTriangle size={14} className="text-amber-600 mt-0.5 shrink-0" />
                            <span><b>Unisci:</b> aggiunge solo quello che manca. <b>Sostituisci:</b> cancella tutto e mette il backup.</span>
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
